import React, {useMemo} from 'react';
import {Link} from 'react-router-dom';
import {Table, Popup, Icon, Form} from 'semantic-ui-react';
import {useTable, usePagination, useFilters, useRowSelect} from 'react-table';
import {Filter, DefaultColumnFilter} from '../common/TableFilters';
import TablePaginator from '../common/TablePaginator';
import DeleteIcon from './icon/DeleteIcon';

export const statusDictionary = {
  'A': 'ACTIVO',
  'I': 'INACTIVO',
  'P': 'PENDIENTE',
};

const STATUS_FILTER_OPTIONS = [
  {'value': 'ALL', 'text': 'TODOS'},
  {'value': 'A', 'text': 'ACTIVO'},
  {'value': 'I', 'text': 'INACTIVO'},
];

const columnsTableWithProps = ({dealerId, isUserFromTDE, type}) => {
  let columns = [];

  if ((isUserFromTDE || type === 'modify') && !columns.length) {
    const userColumn = {
      'Header': 'USUARIO',
      'accessor': 'username',
    };
    columns.push(userColumn);
  }
  columns = columns.concat([
    {
      'Header': 'NOMBRE',
      'accessor': 'name',
    },
    {
      'Header': 'CONCESIONARIO',
      'accessor': 'dealer',
    },
    {
      'Header': 'AGENCIA',
      'accessor': 'agency',
    },
    {
      'Header': 'CORREO',
      'accessor': 'email',
    },
    {
      'Header': 'CREADO',
      'accessor': 'creationDate',
    },
    {
      'id': 'actions',
      'Header': 'ACCIONES',
      'Cell': ({row}) => {
        const {id, username} = row.original;
        const path = type === 'modify' ? `/usuarios/editar/${username}` : `/usuarios-solicitudes/editar/${id}`;

        return (
          <>
            <Popup
              trigger={
                <Link to={path}>
                  <Icon name='write' color='red' size='big' />
                </Link>
              }
              content='Editar'
              position='top center'
              inverted
            />
            {!isUserFromTDE && type !== 'modify' && (
              <DeleteIcon id={id} dealerId={dealerId} isUserFromTDE={isUserFromTDE} />
            )}
          </>
        );
      },
    },
  ]);

  if (type === 'modify' && columns.length === 7) {
    const statusColumn = {
      'Header': 'ESTADO',
      'accessor': 'status',
      'id': 'status',
      'Cell': ({row}) => {
        const {status} = row.original;
        const iconName = status === 'A' ? 'check' : 'close';
        const color = status === 'A' ? 'green' : 'red';
        return <Icon name={iconName} color={color} circular inverted />;
      },
    };
    columns.push(statusColumn);
  }

  return columns;
};

const UsersTable = (props) => {
  const {list} = props;
  const columnsTable = columnsTableWithProps(props);
  const data = useMemo(() => list, [list]);
  const columns = useMemo(() => columnsTable, []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    'state': {pageIndex, pageSize},
  } = useTable(
    {
      columns,
      data,
      'defaultColumn': {'Filter': DefaultColumnFilter},
      'initialState': {'pageIndex': 0, 'pageSize': 10},
      'autoResetFilters': false,
    },
    useFilters,
    usePagination,
    useRowSelect,
    (hooks) => {
      const {statusFilter, onChangeStatusFilter} = props;
      hooks.visibleColumns.push((columns) => [
        ...columns.map((column) => {
          if (column.id === 'status') {
            return {
              ...column,
              'Filter': ({row}) => {
                return (<>
                  <Form.Select
                    placeholder="Buscar..."
                    fluid
                    selection
                    className='selector-tiny-size'
                    value={statusFilter}
                    defaultValue='ALL'
                    onChange={(item, {value}) => onChangeStatusFilter(value)}
                    options={STATUS_FILTER_OPTIONS}
                  />
                </>);
              },
            };
          }
          return column;
        }),
      ]);
    },
  );

  return (
    <>
      <div className="table-container">
        <Table className="interactive" striped {...getTableProps()}>
          <Table.Header>
            {headerGroups.map((headerGroup) => (
              <Table.Row key="headers" {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, index) => (
                  <Table.HeaderCell
                    key={`title-${index}`}
                    textAlign="center"
                    {...column.getHeaderProps()}
                  >
                    {column.render('Header')}
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            ))}
            {headerGroups.map((headerGroup) => (
              <Table.Row key="filters" id="filters">
                {headerGroup.headers.map((column, index) => (
                  <Table.HeaderCell
                    key={`filter-${index}`}
                    textAlign="center"
                  >
                    <Filter column={column} />
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            ))}
          </Table.Header>
          <Table.Body {...getTableBodyProps()}>
            {!page.length && (
              <Table.Row>
                <Table.Cell colSpan={15} textAlign="center">
                  No hay datos disponibles
                </Table.Cell>
              </Table.Row>
            )}
            {page.length > 0 &&
              page.map((row, i) => {
                prepareRow(row);
                return (
                  <Table.Row key={i}>
                    {row.cells.map((cell) => {
                      return (
                        <Table.Cell textAlign="center" {...cell.getCellProps()}>
                          {cell.render('Cell')}
                        </Table.Cell>
                      );
                    })}
                  </Table.Row>
                );
              })}
          </Table.Body>
        </Table>
      </div>
      <TablePaginator
        data={data}
        canPreviousPage={canPreviousPage}
        pageIndex={pageIndex}
        pageOptions={pageOptions}
        pageSize={pageSize}
        canNextPage={canNextPage}
        nextPage={nextPage}
        gotoPage={gotoPage}
        previousPage={previousPage}
        setPageSize={setPageSize}
      />
    </>
  );
};

export default UsersTable;
